import {
  type User,
  type Event,
  type UserSubscription,
  editableEventIsCreatable,
} from '@shady-pines/db';
import { isNonNil, now, strongFromEntries, type Uuid } from '@shady-pines/types';
import { isBefore } from 'date-fns';
import { isEmpty, isNil, omit } from 'lodash';
import { supabaseApi } from '../api/api-store';
import { EventDetailsApi } from './event-details-api';
import { EventVenueApi } from './event-venue-api';
import type { EventState } from './types';
import { calculateEventChanges } from './utils';

export type EventApi = ReturnType<typeof EventApi>;

// TODO: probably can do some better filtering on event selection stuff based on activeness

export const EventApi = (
  get: () => EventState,
  set: (partial: Partial<EventState> | ((state: EventState) => Partial<EventState>)) => void,
) =>
  ({
    fetchEvents: async () => {
      const fetching = get().loading.events;
      if (fetching) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              events: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const { data: events } = await supabaseApi()
        .from('event_events_public')
        .select('*')
        .is('deleted_at', null);
      set(
        (s) =>
          ({
            events: events ?? [],
            lastEventRefresh: now(),
            loading: {
              ...s.loading,
              events: false,
            },
          }) as const satisfies Partial<EventState>,
      );
    },
    fetchEventSubscriptions: async (userId: Uuid<User> | null) => {
      const loading = get().loading.subscriptions;

      if (loading) {
        return;
      }

      if (isNil(userId)) {
        set({
          eventSubscriptionsById: {},
        } as const satisfies Partial<EventState>);
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              subscriptions: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const { data: subscriptions } = await supabaseApi()
        .from('user_subscriptions')
        .select('*')
        .is('deleted_at', null)
        .eq('subscription_type', 'event')
        .eq('user_id', userId);

      const strongSubscriptions = (subscriptions ?? []) as UserSubscription<'event'>[];

      set(
        (s) =>
          ({
            eventSubscriptionsById: strongFromEntries(
              strongSubscriptions.map((i) => [i.event_id, true]),
            ),
            loading: {
              ...s.loading,
              subscriptions: false,
            },
          }) as const satisfies Partial<EventState>,
      );
    },

    updateEvent: async (me: Uuid<User>) => {
      const loading = get().loading.updateEvent;

      if (loading) {
        return;
      }

      const editableVenue = get().editableVenue;
      const editableEvent = get().editableEvent;

      const originalEvent = get().events?.find((e) => e.id === editableEvent?.event.id);
      if (isNil(originalEvent)) {
        return;
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateEvent: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const venueId = await (async () => {
        if (isNil(editableVenue?.name)) {
          return null;
        }

        return EventVenueApi(get, set).createVenue(me);
      })();
      console.log('venu', venueId);

      if (isNil(editableEvent) && isNil(venueId)) {
        console.log('returning bc no editable event or venueid', editableEvent, venueId);
        set(
          (s) =>
            ({
              loading: {
                ...s.loading,
                updateEvent: false,
              },
            }) as const satisfies Partial<EventState>,
        );

        return;
      }

      const comparisonEvent = {
        categories: get().categoriesByEventId?.[originalEvent.id] ?? [],
        users: get().featuredUsersByEventId?.[originalEvent.id] ?? [],
      } as const;

      const eventChanges = calculateEventChanges(comparisonEvent, editableEvent);

      console.log('got changed', eventChanges, comparisonEvent);

      await EventDetailsApi(get, set).updateEventCategories(
        originalEvent.id,
        eventChanges.categoriesToAdd,
        eventChanges.categoriesToRemove,
      );

      console.log('updated categories');

      await EventDetailsApi(get, set).updateEventFeaturedUsers(
        originalEvent.id,
        eventChanges.usersToAdd,
        eventChanges.usersToRemove,
      );

      const { data: updated } = await supabaseApi()
        .from('event_events')
        .update({
          ...omit(editableEvent?.event, 'subscriber_count'),
          venue_id: isNonNil(venueId) ? venueId : undefined,
        })
        .eq('id', originalEvent.id)
        .select('*')
        .single();

      set((s) => {
        const eventIndex = s.events?.findIndex((e) => e.id === originalEvent.id) ?? -1;
        const updatedEvents = [...(s.events ?? [])];
        updatedEvents[eventIndex] = isNil(updated)
          ? (s.events ?? [])[eventIndex]
          : { ...updated, subscriber_count: originalEvent.subscriber_count };

        return {
          loading: {
            ...s.loading,
            updateEvent: false,
          },
          events: updatedEvents,
        } as const satisfies Partial<EventState>;
      });
    },
    createEvent: async (me: Uuid<User>) => {
      const loading = get().loading.createEvent;
      const creatableEvent = get().editableEvent;

      if (loading || isNil(creatableEvent) || !editableEventIsCreatable(creatableEvent.event)) {
        return;
      }
      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              createEvent: true,
            },
          }) as const satisfies Partial<EventState>,
      );

      const createdEvent = await supabaseApi()
        .from('event_events')
        .insert({ ...creatableEvent.event, created_by: me })
        .select('*')
        .single();

      const createdEventData = createdEvent.data;

      if (isNil(createdEventData)) {
        console.error('unable to create event', createdEvent);
        set(
          (s) =>
            ({
              loading: {
                ...s.loading,
                createEvent: false,
              },
            }) as const satisfies Partial<EventState>,
        );
        return createdEvent.error;
      }

      console.log('created event, creating categories and users');
      await EventDetailsApi(get, set).updateEventCategories(
        createdEventData.id,
        creatableEvent.categories ?? [],
      );
      await EventDetailsApi(get, set).updateEventFeaturedUsers(
        createdEventData.id,
        creatableEvent.users ?? [],
      );

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              createEvent: false,
            },
            events: isNil(s.events)
              ? null
              : [
                  ...s.events,
                  {
                    ...createdEventData,
                    subscriber_count: 0,
                  },
                ],
          }) as const satisfies Partial<EventState>,
      );

      return createdEventData.id;
    },
    updateEventSubscription: async (userId: Uuid<User>, id: Uuid<Event>) => {
      const loading = get().loading.updateEventSubscriptionById[id];

      if (loading) {
        return;
      }

      const isSubscribed = get().eventSubscriptionsById?.[id];

      const { error } = await (async () => {
        if (isSubscribed) {
          const { error: subscribeError } = await supabaseApi()
            .from('user_subscriptions')
            .update({
              updated_at: now(),
              deleted_at: now(),
            })
            .eq('user_id', userId)
            .eq('subscription_type', 'event')
            .eq('event_id', id);

          return { error: subscribeError };
        }
        const { error: subscribeError } = await supabaseApi().from('user_subscriptions').insert({
          event_id: id,
          user_id: userId,
          subscription_type: 'event',
        });

        return { error: subscribeError };
      })();

      if (isNil(error)) {
        set(
          (s) =>
            ({
              eventSubscriptionsById: {
                ...s.eventSubscriptionsById,
                [id]: !isSubscribed,
              },
            }) as const satisfies Partial<EventState>,
        );
      }

      set(
        (s) =>
          ({
            loading: {
              ...s.loading,
              updateEventSubscriptionById: {
                ...s.loading,
                [id]: false,
              },
            },
          }) as const satisfies Partial<EventState>,
      );

      return error;
    },

    refreshEvents: async () => {
      const loading = get().loading.events;

      if (loading) {
        return;
      }

      set(
        (s) =>
          ({
            loading: { ...s.loading, events: true },
          }) as const satisfies Partial<EventState>,
      );

      const lastEventRefresh = get().lastEventRefresh;
      const eventIds = get().events!.map((s) => s.id);

      const { count } = await supabaseApi()
        .from('event_events_public')
        .select('*', { count: 'exact', head: true })
        .in('id', eventIds);

      if (count !== eventIds.length) {
        set(
          (s) =>
            ({
              loading: { ...s.loading, events: false },
              lastEventRefresh: now(),
            }) as const satisfies Partial<EventState>,
        );
        return {
          result: 'full-refresh-needed',
        } as const;
      }

      const { data: updatedEvents } = await supabaseApi()
        .from('event_events_public')
        .select('*')
        .is('deleted_at', null)
        .gt('updated_at', lastEventRefresh ?? now())
        .order('start_time', { ascending: true });

      if (isNil(updatedEvents) || isEmpty(updatedEvents)) {
        set(
          (s) =>
            ({
              loading: { ...s.loading, events: false },
              lastEventRefresh: now(),
            }) as const satisfies Partial<EventState>,
        );
        return {
          result: 'no-refresh-required',
        } as const;
      }

      set((s) => {
        const notUpdatedEvents = s.events!.filter((e) => !updatedEvents.some((u) => u.id === e.id));
        return {
          loading: { ...s.loading, events: false },
          events: [...notUpdatedEvents, ...updatedEvents].sort((a, b) =>
            isBefore(a.start_time, b.start_time) ? 1 : 1,
          ),
          lastEventRefresh: now(),
        } as const satisfies Partial<EventState>;
      });

      return {
        result: 'refreshed',
        refreshedIds: updatedEvents.map((u) => u.id),
      } as const;
    },
  }) as const;
